import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { AlertTriangle, ChevronDown, ChevronUp, Gauge, Search, Users } from "lucide-react";
import { apiBaseUrl, authHeaders, formatHm } from "./shared";
import LoadingState from "./LoadingState";

function useDebounced(value, delayMs) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

function groupByDispatcherName(drivers) {
  const byDispatcher = new Map();
  for (const d of drivers) {
    const key = d.dispatcher_name || "Unassigned";
    if (!byDispatcher.has(key)) byDispatcher.set(key, []);
    byDispatcher.get(key).push(d);
  }
  return [...byDispatcher.entries()].sort(([a], [b]) => {
    if (a === "Unassigned") return 1;
    if (b === "Unassigned") return -1;
    return a.localeCompare(b);
  });
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}
// ~6 months back -- matches the backend's own default range when no
// start/end is passed, kept explicit here so the date pickers show the
// real range being queried instead of looking blank on first load.
function sixMonthsAgoIso() {
  const d = new Date();
  d.setDate(d.getDate() - 182);
  return d.toISOString().slice(0, 10);
}
function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });
}
function fmtDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso.endsWith("Z") || iso.includes("+") ? iso : `${iso}Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-US", { month: "2-digit", day: "2-digit", year: "numeric", hour: "numeric", minute: "2-digit" });
}

// Real per-day driving-hours history (driver_hos_daily, backend/motive_sync.py
// -- persisted forever from the same Motive recap data behind the truck
// detail panel's "Duty hours, last 8 days" view, so it survives long after
// Motive's own rolling window scrolls past a day) compared against the real
// FMCSA daily limit (hos_regulations). This is the pattern side of HOS
// compliance -- "who has gone over, and by how much, over time" -- distinct
// from the Alerts page's hos_violation type, which only ever shows a LIVE,
// currently-open violation for today.
export default function HosCompliance() {
  const [start, setStart] = useState(sixMonthsAgoIso());
  const [end, setEnd] = useState(todayIso());
  const [rawSearch, setRawSearch] = useState("");
  const search = useDebounced(rawSearch, 300);
  const [groupByDispatcher, setGroupByDispatcher] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());
  const [expandedCycle, setExpandedCycle] = useState(() => new Set());

  useEffect(() => {
    setLoading(true);
    setError(null);
    const params = { start, end };
    if (search.trim()) params.search = search.trim();
    axios
      .get(`${apiBaseUrl}/scripted/fleet/hos-compliance-summary`, { ...authHeaders(), params })
      .then((res) => setData(res.data))
      .catch((err) => setError(err.response?.data?.detail || err.message))
      .finally(() => setLoading(false));
  }, [start, end, search]);

  function toggle(driverId) {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(driverId) ? next.delete(driverId) : next.add(driverId);
      return next;
    });
  }

  function toggleCycle(driverId) {
    setExpandedCycle((prev) => {
      const next = new Set(prev);
      next.has(driverId) ? next.delete(driverId) : next.add(driverId);
      return next;
    });
  }

  const drivers = data?.drivers || [];
  const totalHoursOver = drivers.reduce((sum, d) => sum + d.total_hours_over, 0);
  const totalDaysOver = drivers.reduce((sum, d) => sum + d.days_over, 0);

  const cycleDrivers = data?.cycle_drivers || [];
  const cycleTotalHoursOver = cycleDrivers.reduce((sum, d) => sum + d.total_hours_over, 0);
  const cycleTotalEpisodes = cycleDrivers.reduce((sum, d) => sum + d.episodes, 0);

  const dailyGroups = useMemo(() => (groupByDispatcher ? groupByDispatcherName(drivers) : null), [groupByDispatcher, drivers]);
  const cycleGroups = useMemo(() => (groupByDispatcher ? groupByDispatcherName(cycleDrivers) : null), [groupByDispatcher, cycleDrivers]);

  return (
    <div>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>HOS Compliance</h1>
          <div style={styles.subtitle}>
            Real historical HOS violations, by driver -- not who's currently over (see Alerts for that), but who has
            a pattern of running over the daily or 8-day cycle limit, and by how much.
          </div>
        </div>
        <div style={styles.dateRange}>
          <input type="date" value={start} onChange={(e) => setStart(e.target.value)} style={styles.dateInput} max={end} />
          <span style={styles.dateSep}>to</span>
          <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} style={styles.dateInput} min={start} max={todayIso()} />
        </div>
      </div>

      <div style={styles.toolbar}>
        <div style={styles.searchWrap}>
          <Search size={14} style={styles.searchIcon} />
          <input
            style={styles.searchInput}
            type="search"
            placeholder="Search by driver name or truck #..."
            value={rawSearch}
            onChange={(e) => setRawSearch(e.target.value)}
          />
        </div>
        <button
          type="button"
          style={{ ...styles.groupBtn, ...(groupByDispatcher ? styles.groupBtnActive : {}) }}
          onClick={() => setGroupByDispatcher((v) => !v)}
        >
          <Users size={13} /> Group by dispatcher
        </button>
      </div>

      {error && <div style={styles.errorBox}>Couldn't load HOS history: {error}</div>}

      {loading && <LoadingState label="Loading HOS history…" />}
      {!loading && (
      <>

      <div style={styles.sectionHead}>
        <h2 style={styles.sectionTitle}>Daily (11h) violations</h2>
        <div style={styles.subtitle}>
          Who has a pattern of running over the {data?.daily_limit_hours ?? 11}-hour daily driving limit, and by how much.
        </div>
      </div>

      <div style={styles.summaryRow}>
        <div style={styles.summaryCard}>
          <div style={styles.summaryValue}>{loading ? "…" : drivers.length}</div>
          <div style={styles.summaryLabel}>Drivers with a violation</div>
        </div>
        <div style={styles.summaryCard}>
          <div style={styles.summaryValue}>{loading ? "…" : totalDaysOver}</div>
          <div style={styles.summaryLabel}>Total violation-days</div>
        </div>
        <div style={styles.summaryCard}>
          <div style={{ ...styles.summaryValue, color: "#dc2626" }}>{loading ? "…" : formatHm(totalHoursOver)}</div>
          <div style={styles.summaryLabel}>Total hours over, fleet-wide</div>
        </div>
      </div>

      {!loading && !error && drivers.length === 0 && (
        <div style={styles.emptyState}>
          <Gauge size={22} color="var(--muted)" />
          <div style={styles.emptyTitle}>
            {rawSearch.trim() ? `No daily-limit violations match "${rawSearch.trim()}"` : "No daily-limit violations in this range"}
          </div>
          <div style={styles.emptySubtitle}>
            {rawSearch.trim() ? "Try a different driver name or truck #, or clear the search." : `Every driver stayed under ${data?.daily_limit_hours ?? 11}h of driving on every logged day.`}
          </div>
        </div>
      )}

      {groupByDispatcher && dailyGroups
        ? dailyGroups.map(([dispatcherName, groupDrivers]) => (
            <div key={dispatcherName} style={styles.dispatcherGroup}>
              <div style={styles.dispatcherGroupHead}>
                <span style={styles.dispatcherGroupName}>{dispatcherName}</span>
                <span style={styles.dispatcherGroupCount}>{groupDrivers.length} driver{groupDrivers.length === 1 ? "" : "s"}</span>
              </div>
              <div style={styles.driverList}>
                {groupDrivers.map((d) => (
                  <HosDriverCard key={d.driver_id} d={d} kind="daily" isOpen={expanded.has(d.driver_id)} onToggle={() => toggle(d.driver_id)} />
                ))}
              </div>
            </div>
          ))
        : drivers.length > 0 && (
            <div style={styles.driverList}>
              {drivers.map((d) => (
                <HosDriverCard key={d.driver_id} d={d} kind="daily" isOpen={expanded.has(d.driver_id)} onToggle={() => toggle(d.driver_id)} />
              ))}
            </div>
          )}

      <div style={styles.sectionHead}>
        <h2 style={styles.sectionTitle}>Cycle (70h / 8-day) violations</h2>
        <div style={styles.subtitle}>
          Real 8-day rolling-cycle violations, by driver -- gated the same way the live Alerts feed is (against
          Motive's own, restart-aware {data?.cycle_limit_hours ?? 70}-hour cycle clock), so a driver who legitimately
          took a 34-hour restart won't show up here just because the calendar window still contains pre-restart hours.
        </div>
      </div>

      <div style={styles.summaryRow}>
        <div style={styles.summaryCard}>
          <div style={styles.summaryValue}>{loading ? "…" : cycleDrivers.length}</div>
          <div style={styles.summaryLabel}>Drivers with a cycle violation</div>
        </div>
        <div style={styles.summaryCard}>
          <div style={styles.summaryValue}>{loading ? "…" : cycleTotalEpisodes}</div>
          <div style={styles.summaryLabel}>Total violation episodes</div>
        </div>
        <div style={styles.summaryCard}>
          <div style={{ ...styles.summaryValue, color: "#dc2626" }}>{loading ? "…" : formatHm(cycleTotalHoursOver)}</div>
          <div style={styles.summaryLabel}>Total hours over, fleet-wide</div>
        </div>
      </div>

      {!loading && !error && cycleDrivers.length === 0 && (
        <div style={styles.emptyState}>
          <Gauge size={22} color="var(--muted)" />
          <div style={styles.emptyTitle}>No cycle-limit violations in this range</div>
          <div style={styles.emptySubtitle}>
            {rawSearch.trim() ? "Try a different driver name or truck #, or clear the search." : `Every driver stayed under ${data?.cycle_limit_hours ?? 70}h of driving in their rolling 8-day window.`}
          </div>
        </div>
      )}

      {groupByDispatcher && cycleGroups
        ? cycleGroups.map(([dispatcherName, groupDrivers]) => (
            <div key={dispatcherName} style={styles.dispatcherGroup}>
              <div style={styles.dispatcherGroupHead}>
                <span style={styles.dispatcherGroupName}>{dispatcherName}</span>
                <span style={styles.dispatcherGroupCount}>{groupDrivers.length} driver{groupDrivers.length === 1 ? "" : "s"}</span>
              </div>
              <div style={styles.driverList}>
                {groupDrivers.map((d) => (
                  <HosDriverCard key={d.driver_id} d={d} kind="cycle" isOpen={expandedCycle.has(d.driver_id)} onToggle={() => toggleCycle(d.driver_id)} />
                ))}
              </div>
            </div>
          ))
        : cycleDrivers.length > 0 && (
            <div style={styles.driverList}>
              {cycleDrivers.map((d) => (
                <HosDriverCard key={d.driver_id} d={d} kind="cycle" isOpen={expandedCycle.has(d.driver_id)} onToggle={() => toggleCycle(d.driver_id)} />
              ))}
            </div>
          )}
      </>
      )}
    </div>
  );
}

function HosDriverCard({ d, kind, isOpen, onToggle }) {
  const meta = [d.unit_number ? `Truck #${d.unit_number}` : null, d.dispatcher_name || null].filter(Boolean).join(" · ");
  return (
    <div style={styles.driverCard}>
      <div style={styles.driverHead} onClick={onToggle}>
        <AlertTriangle size={14} style={{ color: "#dc2626", flexShrink: 0 }} />
        <div style={styles.driverNameWrap}>
          <span style={styles.driverName}>{d.driver_name}</span>
          {meta && <span style={styles.driverMeta}>{meta}</span>}
        </div>
        <span style={styles.driverStat}>
          <b style={{ color: "#dc2626" }}>{formatHm(d.total_hours_over)}</b> total over
        </span>
        {kind === "daily" ? (
          <>
            <span style={styles.driverStatMuted}>{d.days_over} day{d.days_over === 1 ? "" : "s"}</span>
            <span style={styles.driverStatMuted}>worst {formatHm(d.worst_day_hours_over)}</span>
          </>
        ) : (
          <>
            <span style={styles.driverStatMuted}>{d.episodes} episode{d.episodes === 1 ? "" : "s"}</span>
            <span style={styles.driverStatMuted}>worst {formatHm(d.worst_hours_over)}</span>
          </>
        )}
        <span style={{ marginLeft: "auto", color: "var(--muted)" }}>
          {isOpen ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
        </span>
      </div>
      {isOpen && (
        <div style={styles.violationList}>
          {kind === "daily" ? (
            <>
              <div style={styles.violationHeadRow}>
                <span style={styles.violationCol1}>Date</span>
                <span style={styles.violationColNum}>Peak driving hours</span>
                <span style={styles.violationColNum}>Over by</span>
              </div>
              {d.violations.map((v) => (
                <div key={v.date} style={styles.violationRow}>
                  <span style={styles.violationCol1}>{fmtDate(v.date)}</span>
                  <span style={styles.violationColNum}>{formatHm(v.peak_driving_hours)}</span>
                  <span style={{ ...styles.violationColNum, color: "#dc2626", fontWeight: 700 }}>+{formatHm(v.hours_over)}</span>
                </div>
              ))}
            </>
          ) : (
            <>
              <div style={styles.violationHeadRow}>
                <span style={styles.violationCol1}>Detected</span>
                <span style={styles.violationCol1}>Resolved</span>
                <span style={styles.violationColNum}>Over by</span>
              </div>
              {d.violations.map((v, i) => (
                <div key={`${v.detected_at}-${i}`} style={styles.violationRow}>
                  <span style={styles.violationCol1}>{fmtDateTime(v.detected_at)}</span>
                  <span style={styles.violationCol1}>{v.resolved_at ? fmtDateTime(v.resolved_at) : "still open"}</span>
                  <span style={{ ...styles.violationColNum, color: "#dc2626", fontWeight: 700 }}>+{formatHm(v.hours_over)}</span>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

const styles = {
  header: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 20, gap: 20 },
  title: { fontSize: 22, fontWeight: 800, color: "var(--ink)", margin: 0 },
  subtitle: { fontSize: 13, color: "var(--muted)", marginTop: 4, maxWidth: 620 },
  sectionHead: { marginTop: 32, marginBottom: 20 },
  sectionTitle: { fontSize: 17, fontWeight: 800, color: "var(--ink)", margin: "0 0 4px 0" },
  dateRange: { display: "flex", alignItems: "center", gap: 8, flexShrink: 0 },
  dateInput: {
    fontSize: 12.5, color: "var(--ink-2)", background: "var(--card)", border: "1px solid var(--line)",
    borderRadius: 8, padding: "7px 10px",
  },
  dateSep: { fontSize: 12, color: "var(--muted)" },

  errorBox: {
    background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.2)", color: "#dc2626",
    borderRadius: "var(--radius-md)", padding: "12px 16px", fontSize: 13, marginBottom: 16,
  },

  summaryRow: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14, marginBottom: 20 },
  summaryCard: {
    background: "var(--card)", border: "1px solid var(--line)", borderRadius: "var(--radius-lg)",
    padding: "16px 18px", boxShadow: "var(--shadow-xs)",
  },
  summaryValue: { fontSize: 20, fontWeight: 800, color: "var(--ink)", lineHeight: 1.2 },
  summaryLabel: { fontSize: 12, color: "var(--muted)", marginTop: 2 },

  toolbar: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 16 },
  searchWrap: { position: "relative", flex: "1 1 260px", minWidth: 180 },
  searchIcon: { position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--muted)" },
  searchInput: {
    width: "100%", boxSizing: "border-box", fontSize: 12.5, padding: "8px 10px 8px 30px",
    borderRadius: 8, border: "1px solid var(--line)", background: "var(--card)", color: "var(--ink)",
  },
  groupBtn: {
    display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600,
    color: "var(--ink-2)", background: "var(--card)", border: "1px solid var(--line)",
    borderRadius: 8, padding: "7px 12px", cursor: "pointer", flexShrink: 0, whiteSpace: "nowrap",
  },
  groupBtnActive: { color: "var(--brand)", borderColor: "var(--brand)", background: "rgba(37,99,235,0.06)" },

  dispatcherGroup: { marginBottom: 16 },
  dispatcherGroupHead: { display: "flex", alignItems: "baseline", gap: 8, padding: "4px 2px 8px" },
  dispatcherGroupName: { fontSize: 13, fontWeight: 700, color: "var(--ink)" },
  dispatcherGroupCount: { fontSize: 11.5, color: "var(--muted)" },

  emptyState: {
    display: "flex", flexDirection: "column", alignItems: "center", gap: 6, textAlign: "center",
    background: "var(--card)", border: "1px solid var(--line)", borderRadius: "var(--radius-lg)", padding: "40px 20px",
  },
  emptyTitle: { fontSize: 14, fontWeight: 700, color: "var(--ink)" },
  emptySubtitle: { fontSize: 12.5, color: "var(--muted)" },

  driverList: { display: "flex", flexDirection: "column", gap: 10 },
  driverCard: {
    background: "var(--card)", border: "1px solid var(--line)", borderRadius: "var(--radius-lg)",
    boxShadow: "var(--shadow-xs)", overflow: "hidden",
  },
  driverHead: { display: "flex", alignItems: "center", gap: 10, cursor: "pointer", padding: "12px 14px" },
  driverNameWrap: { display: "flex", flexDirection: "column", gap: 1 },
  driverName: { fontSize: 13.5, fontWeight: 800, color: "var(--ink)" },
  driverMeta: { fontSize: 11, color: "var(--muted)" },
  driverStat: { fontSize: 12.5, color: "var(--ink-2)" },
  driverStatMuted: { fontSize: 12, color: "var(--muted)" },

  violationList: { borderTop: "1px solid var(--line)", padding: "10px 14px" },
  violationHeadRow: {
    display: "flex", fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase",
    letterSpacing: 0.3, padding: "4px 0", borderBottom: "1px solid var(--line)", marginBottom: 4,
  },
  violationRow: { display: "flex", fontSize: 12.5, color: "var(--ink-2)", padding: "5px 0" },
  violationCol1: { flex: 1 },
  violationColNum: { width: 140, textAlign: "right" },
};
